import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type ButtonProps,
  type CalendarProps,
} from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type DateFilterProps = {
  variant?: ButtonProps['variant']
  className?: string
  fromDate?: Date
  disabled?: (date: Date) => boolean
  outsideRangeMessage?: string
  calendarProps?: Omit<CalendarProps, 'mode' | 'selected' | 'onSelect'>
  defaultDate?: Date
  onSelect?: (date: Date | undefined) => void
}

export function DateFilter(props: DateFilterProps) {
  const { t, i18n } = useTranslation('common')
  const { formatDateLong } = useLocalizedDate()
  const [date, setDate] = useState<Date | undefined>(props.defaultDate)
  const [month, setMonth] = useState<Date>(new Date())

  useEffect(() => {
    setDate(props.defaultDate)
  }, [props.defaultDate])

  const isMonthOutsideRange = () => {
    const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0)

    // Check if all days in the month are disabled
    for (let day = new Date(monthStart); day <= monthEnd; day.setDate(day.getDate() + 1)) {
      if (!props.disabled || !props.disabled(new Date(day))) {
        // If we find any date that's not disabled, return false
        return false
      }
    }

    // If we don't find any enabled dates, the entire month is disabled
    return true
  }

  const handleSelect = (newDate: Date | undefined) => {
    setDate((prev) => (!newDate ? prev : newDate))

    if (props.onSelect) {
      props.onSelect(newDate)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={props.variant} className={cn('w-44', props.className)}>
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            <span className="w-full text-center">{formatDateLong(date)}</span>
          ) : (
            <span className="w-full text-center">{t('datePicker.allTime')}</span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0">
        <div className="relative">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(date) => handleSelect(date)}
            initialFocus
            month={month}
            onMonthChange={setMonth}
            disabled={props.disabled}
            required
            language={i18n.language}
            {...props.calendarProps}
          />
          {isMonthOutsideRange() && (
            <div className="bg-background/60 absolute bottom-0 top-12 flex items-center justify-center backdrop-blur-sm">
              <p className="text-muted-foreground mb-6 p-6 text-center text-sm">
                {props.outsideRangeMessage || t('datePicker.outsideRangeMessage')}
              </p>
            </div>
          )}
        </div>
        <div className="p-3 pt-0">
          <Button
            onClick={() => handleSelect(undefined)}
            className="w-full"
            variant={date ? 'secondary' : 'default'}
          >
            {t('datePicker.allTime')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
